import LandingFooter from '@/components/LandingFooter.tsx'
import LandingHeader from '@/components/LandingHeader.tsx'
import styles from '@/pages/PolicyPage.module.scss'
import { languageBasedOnTimezone } from '@/utils/language.ts'
import { useEffect } from 'react'

export const TermsOfUsePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <div className={styles.policyContainer}>
        <div className={styles.topSection}>
          <LandingHeader language={languageBasedOnTimezone()} selectLanguageEnabled={false} />
        </div>
        <div className={styles.contentsSection}>
          <div className={styles.titleContainer}>
            <h1>TRACKER BOOT TERMS OF USE</h1>
            <div className={styles.dates}>Effective Date: February 25, 2025</div>
            <div className={styles.dates}>Last Updated: April 24, 2025</div>
          </div>
          <p>
            Welcome to Tracker Boot, an agile project management platform operated by Bekind Labs LLC ("Bekind Labs,"
            "we," "us," or "our"). These Terms of Use ("Terms") govern your access to and use of our website,
            application, and related services (collectively, the "Service"). By accessing or using Tracker Boot, you
            agree to be bound by these Terms. If you do not agree, you must refrain from accessing or using the Service.
            These Terms apply to all visitors and users of Tracker Boot, including those using free services, paid
            accounts, trial accounts, alpha/beta testers, or anyone using the Service for development testing purposes.
            For detailed information on your legal rights, responsibilities, and our service terms, please refer to our
            <a href="https://trackerboot.com/policies/trackerboot-agreement">&nbsp;Terms of Service</a>.
          </p>
          <div className={styles.contentsContainer}>
            <h2>1.Use of the Service</h2>
            <ul>
              <li>
                <span>Acceptable Use</span>You may access and use Tracker Boot for lawful purposes only. You agree not
                to misuse, interfere with, or attempt to gain unauthorized access to any part of the Service or Service.
                You may not reproduce, duplicate, copy, sell, resell, or exploit any portion of the Service without
                express permission from Bekind Labs.
              </li>
              <li>
                <span>Prohibited Activities</span>You must not post or share content that is offensive, illegal,
                defamatory, or violates third-party rights. You may not upload viruses, malware, or any code designed to
                disrupt the proper operation of Tracker Boot. Attempts to hack, reverse-engineer, or manipulate the
                functionality of Tracker Boot are prohibited. You must not violate intellectual property laws by using
                copyrighted material without permission.
              </li>
              <li>
                <span>Age Restriction</span>Tracker Boot is intended for users aged 16 and above. However, users under
                16 may access the Service with explicit parental or legal guardian consent or under the supervision of
                an authorized educational institution. If a user under 16 wishes to use the Service, they must either
                obtain approval from a parent or guardian or be enrolled in an educational program that oversees their
                use of Tracker Boot. If we learn that a user under 16 has provided personal data without such consent or
                supervision, we will take steps to delete the information.
              </li>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>2. User Accounts and Security</h2>
            <p>
              <span>Account Creation and Responsibilities</span>To use the Service, you are required to create an
              account. You are responsible for maintaining the confidentiality of your login credentials and all
              activities under your account. You must notify Bekind Labs immediately if you suspect unauthorized access
              to your account.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>3. Intellectual Property</h2>
            <p>
              <span>Ownership</span>Bekind Labs retains all rights, title, and interest in Tracker Boot, including its
              software, design, branding, and content. You may not use Tracker Boot's name, logo, or branding without
              prior written consent.
            </p>
            <p>
              <span>User Content</span>You retain ownership of any content, data, or materials uploaded to Tracker Boot
              (“Your Content”). By using Tracker Boot, you grant Bekind Labs a non-exclusive, royalty-free license to
              use, store, and process Your Content solely for service functionality. You are responsible for ensuring
              Your Content complies with applicable laws. Bekind Labs acts only as a passive conduit for online
              distribution and publication of Your Content. While you and authorized users retain full ownership of Your
              Content, Bekind Labs’ access and usage rights are strictly limited to those explicitly granted under this
              agreement. In accordance with applicable data privacy laws, Bekind Labs acts as a data processor, while
              you, as the data controller, remain responsible for ensuring compliance with legal obligations regarding
              Your Content, including its security and integrity.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>4. Evaluation Trial</h2>
            <p>
              <span>Terms</span>During the Evaluation Trial, you may create and manage an unlimited number of Projects
              and Project Members within your Account, with full access to all features of the Service. No credit card
              is required for participation in the Evaluation Trial. At the conclusion of the Evaluation Trial, if you
              do not upgrade to a paid Plan, your Account will automatically transition to the free Plan as outlined on
              the Plans and Billing Page. If your Account exceeds the limitations of the free Plan, it will be placed in
              a read-only mode until either (a) the number of Projects and Project Members is reduced to fit within the
              free Plan limits or (b) you choose to upgrade to a paid Plan that accommodates your Account’s usage. You
              may upgrade to a paid Plan at any time during the Evaluation Trial; however, Bekind Labs will not charge
              you until the trial period ends unless otherwise stated on the Plans and Billing Page.
            </p>
            <p>
              <span>Eligibility</span>Each user is eligible for only one Evaluation Trial per Account. If you create
              additional Accounts, they will automatically begin on the free Plan without an Evaluation Trial. Projects
              cannot be transferred between Accounts that are currently under an Evaluation Trial. If you represent an
              Organization, only one designated representative of that Organization is permitted to claim an Evaluation
              Trial. In cases where your Organization is enrolled in an enterprise Account based on domain-level
              management (e.g., all emails ending with @yourcompany.com), Evaluation Trials may be restricted to one per
              domain. In such cases, users sharing the same domain (e.g., @bekindlabs.com) may not be eligible for
              individual trials, even if they register separate personal Accounts. Please refer to your Organization’s
              account administrator or contact Bekind Labs for clarification.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>5. Alpha/Beta Features and Testing Terms</h2>
            <p>
              We may make certain features or functionality within the Service available to you on an alpha/beta basis.
              By participating in an alpha/beta test, you acknowledge and agree to the following terms and conditions:
            </p>
            <h3>Nature of Alpha/Beta Features</h3>
            <ul>
              <li>Alpha/beta features are experimental and may contain errors, bugs, or incomplete functionality.</li>
              <li>
                These features are provided on an “AS-IS” basis, without indemnification or support, and Bekind Labs
                disclaims all warranties, whether express or implied, including but not limited to warranties of
                merchantability, fitness for a particular purpose, and non-infringement.
              </li>
              <li>
                Alpha/Beta participation does not guarantee that the tested features will be released as part of the
                Service in the future.
              </li>
            </ul>
            <h3>Terms of Use (User Conduct & Confidentiality)</h3>
            <ul>
              <li>
                User Responsibilities: As an alpha/beta tester, you agree to provide feedback, report issues, and comply
                with the general Terms of Use for the Service.
              </li>
              <li>
                Prohibited Activities: You may not attempt to exploit, reverse-engineer, or misuse alpha/beta features.
              </li>
              <li>
                Confidentiality: If the alpha/beta program is private or invite-only, you agree not to disclose any
                details about the alpha/beta features, functionality, or related information to any unauthorized
                individuals, including third parties, employees, contractors, affiliates, or the general public. You may
                only share such information with individuals who have been expressly authorized by Bekind Labs to
                participate in the alpha/beta program. Violation of this confidentiality obligation may result in
                immediate removal from the alpha/beta program, revocation of access to alpha/beta features, and
                potential further action at Bekind Labs’ discretion.
              </li>
            </ul>
            <h3>Terms of Service (Liability & Disclaimer)</h3>
            <ul>
              <li>
                Alpha/beta testing participation is voluntary and does not create any contractual obligation between you
                and Bekind Labs.
              </li>
              <li>
                Bekind Labs is not liable for any data loss, disruptions, compatibility issues, or other unintended
                consequences arising from the use of alpha/beta features.
              </li>
              <li>
                You understand that alpha/beta features are in development and may be modified, discontinued, or removed
                at any time without notice.
              </li>
            </ul>
            <h3>Privacy Policy (Data Collection & Usage)</h3>
            <li>
              Data Collection: During the alpha/beta test, we may collect usage data, feedback, logs, and performance
              metrics to improve the Service.
            </li>
            <li>
              Data Protection: Any personal data collected during the alpha/beta program will be handled in accordance
              with our Privacy Policy at https://bekindlabs.com/privacy-policy.
            </li>
            <li>
              User Consent: By participating, you consent to the collection, processing, and storage of such data. If
              you wish to opt out of testing or request data removal, you may contact us at trackerboot@bekindlabs.com.
              Your participation in alpha/beta testing signifies that you understand and accept these terms. If you do
              not agree, you must refrain from using alpha/beta features.
            </li>
          </div>
          <div className={styles.contentsContainer}>
            <h2>6. Third-Party Services and Links</h2>
            <p>
              <span>Third-Party Integrations</span>
              Tracker Boot may provide integrations with third-party services such as Google, Slack, and GitHub. Use of
              such services is subject to their respective terms of service.
            </p>
            <p>
              <span>External Links</span>Our Service may contain links to third-party websites. We do not endorse or
              assume responsibility for any third-party content, policies, or practices.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>7. Privacy and Data Protection</h2>
            <p>
              <span>Privacy Policy</span>
              Your use of Tracker Boot is subject to our Privacy Policy at
              https://trackerboot.com/policies/privacy-policy.
            </p>
            <p>
              <span>Data Storage and Security</span>
              We implement reasonable security measures to protect user data, but no system is entirely secure. You are
              responsible for safeguarding sensitive information stored in Tracker Boot. Your data may be stored and
              processed in Japan, South Korea, India, or other jurisdictions where Bekind Labs or its trusted partners
              operate. For more details on how we collect, process, and protect your data, please refer to our Terms of
              Service, and Data Processing Addendum.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>8. User-Provided API Tokens and Data Migration</h2>
            <p>
              Tracker Boot allows users to migrate data from a specific project management tool (e.g. Pivotal Tracker)
              by voluntarily providing their API tokens. By doing so, users authorize Bekind Labs to access, retrieve,
              and transfer their project data, backlog items, tasks, and related content from the tool to Tracker Boot.
              Users bear full responsibility for ensuring their API tokens are valid and that they have the necessary
              authorization to permit Bekind Labs to perform the migration.
            </p>
            <p>
              Bekind Labs does not store or retain API tokens beyond the data transfer process unless explicitly
              requested by the user. As a neutral facilitator, Bekind Labs does not guarantee the integrity, accuracy,
              or security of migrated data and is not liable for any loss, corruption, or modification of data resulting
              from the migration process. Users acknowledge that data transfer is subject to the tool’s API
              functionality, availability, and limitations, which may affect the completeness or success of the
              migration.
            </p>
            <p>
              Users are responsible for reviewing and verifying the accuracy and completeness of the migrated data.
              Bekind Labs is not liable for discrepancies, missing data, or modifications occurring during or after the
              migration process. The data migrated from the tool remains the sole property of the user or their
              organization. Bekind Labs does not claim ownership of, nor take responsibility for, the content
              transferred through the migration process.
            </p>
            <p>
              Users accept the risks associated with providing their API tokens. Bekind Labs is not responsible for
              unauthorized access caused by lost, leaked, or misused API tokens. By providing their API tokens, users
              confirm they have the authority to grant Bekind Labs access to perform the migration. Bekind Labs reserves
              the right to reject migration requests if an API token appears invalid, compromised, or in violation of
              security policies.
            </p>
            <p>
              Additionally, users acknowledge that the migration process relies on the tool’s API, which may be subject
              to changes, restrictions, or service termination beyond Bekind Labs’ control. To prevent potential data
              loss, users are strongly advised to back up their data before initiating migration, as Bekind Labs is not
              responsible for any lost, corrupted, or missing data due to API limitations, service outages, or other
              external factors.
            </p>
            <p>
              Bekind Labs reserves the right to modify, suspend, or discontinue the migration service at any time,
              without prior notice, for reasons including but not limited to security concerns, API restrictions, or
              business decisions. Bekind Labs is not liable for any consequences resulting from service suspension or
              termination.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>9. Disclaimers and Limitation of Liability</h2>
            <p>
              <span>No Warranties</span>
              Tracker Boot is provided “as is” and “as available” without warranties of any kind. Bekind Labs does not
              guarantee error-free operation, uninterrupted access, or that defects will be corrected.
            </p>
            <p>
              <span>Limitation of Liability</span>
              To the fullest extent permitted by law, Bekind Labs is not liable for any indirect, incidental, or
              consequential damages arising from the use of Tracker Boot. This includes loss of data, business
              interruptions, or system failures. Bekind Labs shall not be liable for any losses or damages caused or
              alleged to have been caused by the unauthorized use of any User account by a third party. Our maximum
              liability to you for any breach of these terms is one hundred us dollars (USD $100) in the aggregate. The
              foregoing disclaimers will not apply to the extent prohibited by law.
            </p>
            <p>
              <span>Pivotal Tracker</span>
              Pivotal Tracker is a registered trademark of another company. ​​Bekind Labs does not own or control Pivotal
              Tracker and is not responsible for any disruptions, API changes, service outages, or the termination of
              Pivotal Tracker that may impact the data migration process. Users acknowledge that any limitations imposed
              by Pivotal Tracker, including its scheduled termination on April 30, 2025, may affect the completeness,
              accuracy, or availability of transferred data. Bekind Labs is not liable for any failure to migrate data
              due to Pivotal Tracker’s discontinuation or any restrictions placed on its API before its termination.
              Users are strongly encouraged to complete their data migration before the service is discontinued.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>10. Modifications and Termination</h2>
            <p>
              <span>Changes to These Terms</span>
              Bekind Labs reserves the right to modify these Terms at any time. If changes are significant, we will
              provide notice via email or on the Service. It is your responsibility to periodically review the Service
              platform for updates. Your continued use of Tracker Boot after such modifications constitutes your
              acceptance of the updated Terms. If any material change negatively affects your use of the Service, you
              may terminate your account within ten (10) days of notice by emailing trackerboot@bekindlabs.com. In such
              cases, Bekind Labs will provide a prorated refund for any prepaid subscription fees.
            </p>
            <p>
              <span>Termination of Service and Accounts</span>
              Bekind Labs may discontinue, suspend, or terminate Tracker Boot or your account at its sole discretion,
              including if you violate these Terms, pose a security risk, or if required by law, to protect system
              integrity, prevent misuse, or due to circumstances beyond our control. Where feasible, we will provide
              reasonable prior notice before suspension or discontinuation of service. If Tracker Boot is discontinued,
              we will make reasonable efforts to allow you to retrieve your data before deletion. You may terminate your
              account at any time through your account settings. Upon termination, your access to the Service will
              cease, and we may delete your account data in accordance with our data retention policies.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>11. Governing Law and Dispute Resolution</h2>
            <p>
              <span>Governing Law</span>
              These Terms are governed by and construed under the laws of Japan, without regard to conflict of law
              principles.
            </p>
            <p>
              <span>Dispute Resolution</span>
              Any dispute arising from these Terms shall first be attempted to be resolved amicably through
              negotiations. If no resolution is reached, disputes shall be submitted to binding arbitration in Tokyo,
              Japan.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>12. Contact Information</h2>
            <p>
              If you have any questions regarding these Terms, please contact us at
              <a href={'mailto:"trackerboot@bekindlabs.com"'}>&nbsp;trackerboot@bekindlabs.com</a>.
            </p>
          </div>
        </div>
      </div>
      <LandingFooter />
    </>
  )
}
